import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Button,
  Chip,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedIcon from '@mui/icons-material/Verified';
import img1 from './images/hubungi.jpg';
import juniusProfile from './images/junius-profile.jpeg';
import { Helmet } from 'react-helmet-async';

function Contact() {
  const sections = useRef([]);
  const [visibleSections, setVisibleSections] = useState({});

  const images = [
    {
      src: img1,
      title: 'HUBUNGI KAMI',
    },
  ];

  useEffect(() => {
    const currentSections = sections.current;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSections((prev) => {
          const updated = { ...prev };
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              updated[entry.target.dataset.index] = true;
            }
          });
          return updated;
        });
      },
      { threshold: 0.35 },
    );

    currentSections.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  return (
    <Box>
      <Helmet>
        <title>Kontak Dealer Resmi BYD BSD Serpong | Info & Lokasi</title>
        <meta
          name='description'
          content='Hubungi dealer resmi BYD BSD Serpong sekarang untuk informasi harga, promo, test drive, dan pemesanan mobil listrik BYD Sealion 7, Seagull, Denza D9, dan lainnya.'
        />
      </Helmet>

      {/* HERO */}
      {images.map((img, index) => (
        <Box
          key={index}
          ref={(el) => (sections.current[index] = el)}
          data-index={index}
          sx={{
            position: 'relative',
            height: { xs: '36vh', sm: '46vh', md: '56vh' },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            component='img'
            src={img.src}
            alt={`Image ${index + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(0,0,0,0.28))',
            }}
          />

          {img.title && (
            <Box
              sx={{
                position: 'absolute',
                top: { xs: '38%', sm: '38%', md: '40%' },
                left: '50%',
                transform: visibleSections[index]
                  ? 'translate(-50%, 0)'
                  : 'translate(-50%, 50px)',
                opacity: visibleSections[index] ? 1 : 0,
                transition: 'all 0.8s ease-in-out',
                color: '#fff',
                textAlign: 'center',
                px: 2,
              }}
            >
              <Typography
                variant='h1'
                fontWeight='bold'
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  letterSpacing: 1,
                }}
              >
                {img.title}
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      {/* CONTENT */}
      <Box
        sx={{
          backgroundColor: '#f6f6f6',
          px: { xs: 2, sm: 4, md: 6 },
          pt: { xs: 4, sm: 6, md: 8 },
          pb: { xs: 6, sm: 8, md: 10 },
        }}
      >
        <Box
          sx={{
            maxWidth: '1280px',
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: { xs: 3, md: 4, lg: 5 },
            alignItems: 'stretch',
          }}
        >
          {/* LEFT CARD */}
          <Card
            sx={{
              borderRadius: '18px',
              boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
              minHeight: { xs: 'auto', md: 420 },
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography
                variant='h4'
                fontWeight='bold'
                gutterBottom
                sx={{ fontSize: { xs: '1.8rem', md: '2rem' } }}
              >
                Hubungi Kami
              </Typography>

              <Divider sx={{ my: 2.5 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <WhatsAppIcon sx={{ color: '#25D366', mr: 2 }} />
                <Typography sx={{ fontSize: '1rem' }}>
                  <a
                    href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20mau%20tau%20info%20BYD%20nya'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    0812 8686 4825
                  </a>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <InstagramIcon sx={{ color: '#C13584', mr: 2 }} />
                <Typography sx={{ fontSize: '1rem' }}>
                  <a
                    href='https://www.instagram.com/bydbsdserpong/'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    @bydbsdserpong
                  </a>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <BusinessIcon sx={{ color: '#757575', mr: 2 }} />
                <Typography sx={{ fontSize: '1rem' }}>
                  <a
                    href='https://www.google.com/maps/place/BYD+Arista+BSD+City/@-6.2816255,106.6342924,17z/data=!3m1!4b1!4m6!3m5!1s0x2e69fb861088715d:0xcd66cbac6d05d9d0!8m2!3d-6.2816308!4d106.6368673!16s%2Fg%2F11svrps209'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    BYD Arista BSD City
                  </a>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LocationOnIcon sx={{ color: '#F44336', mr: 2, mt: '2px' }} />
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.8 }}>
                  BSD City Serpong Kavling De Park, Jl. BSD Raya Utama Lot 1,
                  Lengkong Kulon, Kec. Pagedangan, Kabupaten Tangerang, Banten
                  15331
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* RIGHT CARD */}
          <Card
            sx={{
              borderRadius: '18px',
              overflow: 'hidden',
              boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
              minHeight: { xs: 'auto', md: 420 },
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #102b8c 0%, #2952d1 100%)',
                color: '#fff',
                px: 3,
                py: 2.5,
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 800 }}>
                Sales Consultant Resmi BYD
              </Typography>
              <Typography sx={{ fontSize: '0.95rem', opacity: 0.92, mt: 0.5 }}>
                Siap bantu info harga, promo, dan test drive
              </Typography>
            </Box>

            <Box
              sx={{
                p: { xs: 3, md: 4 },
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
                gap: { xs: 3, sm: 4 },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f4f7fb',
                  borderRadius: '14px',
                  p: 2,
                  minHeight: 280,
                }}
              >
                <Box
                  component='img'
                  src={juniusProfile}
                  alt='Junius BYD BSD Serpong'
                  sx={{
                    width: '100%',
                    maxWidth: 180,
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{ color: '#777', fontSize: '0.95rem', mb: 0.5 }}
                >
                  Name
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '2rem', md: '2.3rem' },
                    fontWeight: 800,
                    color: '#111827',
                    lineHeight: 1.1,
                    mb: 1.5,
                  }}
                >
                  Junius
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <Chip
                    icon={<VerifiedIcon />}
                    label='Certified'
                    size='small'
                    sx={{
                      backgroundColor: '#e9f1ff',
                      color: '#1747c7',
                      fontWeight: 700,
                    }}
                  />
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Typography
                  sx={{ color: '#777', fontSize: '0.95rem', mb: 0.5 }}
                >
                  Joined
                </Typography>

                <Typography
                  sx={{
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    color: '#111827',
                    mb: 4,
                  }}
                >
                  Januari 2025
                </Typography>

                <Box sx={{ mt: 'auto' }}>
                  <Button
                    variant='contained'
                    fullWidth
                    href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20mau%20konsultasi%20tentang%20mobil%20BYD'
                    target='_blank'
                    sx={{
                      py: 1.35,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                      backgroundColor: '#1639c8',
                      '&:hover': {
                        backgroundColor: '#102ca0',
                      },
                    }}
                  >
                    Consultation
                  </Button>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

export default Contact;
